// Type-only, so this does not create an import cycle with app-branding.service,
// which reads its branding through config() below.
import type { BrandingType } from './_services/branding/app-branding.service';

/**
 * Configuration that varies by **deployment** rather than by build.
 *
 * An Angular build inlines `environment.*` into the bundle, so nothing set there can be
 * changed without rebuilding — which would mean a separate image per deployment. These
 * values therefore live outside the build entirely: the container renders them into
 * `config.json` from `APP_*` environment variables at start-up (see
 * `compose/config.json.template`), and `main.ts` fetches that file before the app boots.
 *
 * `src/environments/*` is for the other kind of setting: things fixed at build time, like
 * `production` or the API base URL.
 */
export interface RuntimeConfig {
  branding: BrandingType;
  auth: {
    /** Mirrors the backend's `DGATE_SOCIAL_LOGIN_ONLY`, inverted. When false the password
     * form, the change-password page and its menu entry are all hidden. */
    passwordLoginEnabled: boolean;
    /** Client id of *this app's* Okta application — a public client, so not a secret.
     * Empty offers no Okta login. See dgate/docs/social_login.md. */
    oktaClientId: string;
  };
  sentry: {
    /** Empty disables Sentry entirely. */
    dsn: string;
    environment: string;
    enableLogs: boolean;
    sendDefaultPii: boolean;
  };
  campaignTrackerEnabled: boolean;
  campaignConnectorEnabled: boolean;
  campaignPlannerEnabled: boolean;
}

/**
 * Used whenever no `config.json` is served: `ng serve` and the unit tests, neither of
 * which has a container to render one. These are the development settings — every feature
 * on, no Sentry, no provider login — which double as a usable last resort for a container
 * whose config failed to render, since a blank app would be worse.
 *
 * The e2e harness does serve a `config.json`, built from `E2E_SPA_CLIENT_ID`; see
 * `dgate/tests/e2e/conftest.py`.
 */
const FALLBACK_CONFIG: RuntimeConfig = {
  branding: 'wpp',
  auth: {
    passwordLoginEnabled: true,
    oktaClientId: '',
  },
  sentry: {
    dsn: '',
    environment: '',
    enableLogs: false,
    sendDefaultPii: false,
  },
  campaignTrackerEnabled: true,
  campaignConnectorEnabled: true,
  campaignPlannerEnabled: true,
};

let resolved: RuntimeConfig | null = null;

/**
 * Loads `config.json` over the fallback. Called once from `main.ts`, before the app is
 * bootstrapped.
 *
 * A missing or malformed file is deliberately not an error — see `FALLBACK_CONFIG`.
 */
export async function loadRuntimeConfig(): Promise<void> {
  try {
    // Relative, so it resolves against the document base in every deployment.
    const response = await fetch('config.json', { cache: 'no-store' });
    // The dev server answers unknown paths with index.html, so a 200 proves nothing;
    // parsing does, and a failure here falls through to the fallback.
    const overrides = response.ok ? ((await response.json()) as Partial<RuntimeConfig>) : {};
    resolved = {
      ...FALLBACK_CONFIG,
      ...overrides,
      auth: { ...FALLBACK_CONFIG.auth, ...overrides.auth },
      sentry: { ...FALLBACK_CONFIG.sentry, ...overrides.sentry },
    };
  } catch {
    resolved = FALLBACK_CONFIG;
  }
}

/**
 * The resolved configuration. A function rather than an exported object so that no build
 * step can inline a deployment-specific value back into the bundle.
 *
 * Falls back when nothing has been loaded, which is what unit tests get: they never run
 * `main.ts`, and several modules — `app.routes.ts`, `sidenav-utils.ts` — read the config
 * at module scope, so throwing here would break any spec that imports them. In the app
 * itself the question does not arise: `main.ts` awaits `loadRuntimeConfig()` before
 * importing the graph that reads it.
 */
export function config(): RuntimeConfig {
  resolved ??= FALLBACK_CONFIG;
  return resolved;
}
